import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";

//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBackwardFast } from "@fortawesome/free-solid-svg-icons";
import { faForwardFast } from "@fortawesome/free-solid-svg-icons";
import { faMoon } from "@fortawesome/free-solid-svg-icons";
import { faSun } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlassPlus } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlassMinus } from "@fortawesome/free-solid-svg-icons";

// Remove the problematic CSS imports - we'll handle styling ourselves
// import "react-pdf/dist/Page/AnnotationLayer.css";
// import "react-pdf/dist/Page/TextLayer.css";

// Set workerSrc for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PDFViewer = ({
  pdfUrl = null,
  fileName = "Document.pdf",
  initialZoom = 100,
  enableToolbar = true,
  enableSearch = true,
  enableDownload = true,
  enablePrint = true,
  theme = "dark", // 'light' or 'dark'
  onDocumentLoad,
  onPageChange,
  onZoomChange,
  onError,
  customToolbarButtons = [],
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [fitMode, setFitMode] = useState("fit-width");
  const [themeState, setTheme] = useState(theme); // to manage theme changes
  const [viewerSize, setViewerSize] = useState({ width: 700, height: 600 });

  const viewerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Theme styles
  const themes = {
    light: {
      background: "#e9e9e9ff",
      toolbar: "#f8f9fa",
      border: "#e9ecef",
      text: "#212529",
      textSecondary: "#6c757d",
      button: "#ffffff",
      buttonHover: "#f8f9fa",
      accent: "#0d6efd",
    },
    dark: {
      background: "#2d3748ff",
      toolbar: "#1a202c",
      border: "#4a5568",
      text: "#f7fafc",
      textSecondary: "#a0aec0",
      button: "#4a5568",
      buttonHover: "#2d3748",
      accent: "#4299e1",
    },
  };
  const currentTheme = themes[themeState];

  // PDF.js document load handler
  const handleDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);
    setError(null);
    if (onDocumentLoad) onDocumentLoad({ pages: numPages, fileName });
  };

  const handleDocumentLoadError = (err) => {
    console.error("PDF Load Error:", err);
    setError(err.message || "Failed to load PDF");
    if (onError) onError(err);
  };

  // Navigation
  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
    if (onPageChange) onPageChange(newPage);
  };
  const nextPage = () => goToPage(currentPage + 1);
  const prevPage = () => goToPage(currentPage - 1);
  const firstPage = () => goToPage(1);
  const lastPage = () => goToPage(totalPages);

  // Zoom
  const handleZoom = (newZoom) => {
    const zoom = Math.max(25, Math.min(500, newZoom));
    setZoomLevel(zoom);
    if (onZoomChange) onZoomChange(zoom);
  };
  const zoomIn = () => handleZoom(zoomLevel + 5);
  const zoomOut = () => handleZoom(zoomLevel - 5);
  const resetZoom = () => handleZoom(100);

  // Fit modes (simple implementation)
  const handleFitMode = (mode) => {
    setFitMode(mode);
    switch (mode) {
      case "fit-width":
        handleZoom(100);
        break;
      case "fit-height":
        handleZoom(120);
        break;
      case "fit-page":
        handleZoom(80);
        break;
      case "actual-size":
        handleZoom(100);
        break;
    }
  };

  // Search (not implemented, placeholder)
  const handleSearch = (term) => {
    setSearchTerm(term);
    // You can implement PDF text search using PDF.js text layer if needed
  };
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  };

  // Download
  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Print
  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl);
      printWindow?.addEventListener("load", () => {
        printWindow.print();
      });
    }
  };
  //toggle theme
  const handleThemeToggle = () => {
    setTheme(themeState === "light" ? "dark" : "light");
  };
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT") return;
      switch (e.key) {
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          prevPage();
          break;
        case "ArrowRight":
        case "PageDown":
          e.preventDefault();
          nextPage();
          break;
        case "Home":
          e.preventDefault();
          firstPage();
          break;
        case "End":
          e.preventDefault();
          lastPage();
          break;
        case "+":
        case "=":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomIn();
          }
          break;
        case "-":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            zoomOut();
          }
          break;
        case "0":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            resetZoom();
          }
          break;
        case "f":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            toggleSearch();
          }
          break;
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, totalPages, zoomLevel]);

  // Custom CSS for PDF text and annotation layers
  const pdfStyles = `
    .react-pdf__Page__textContent {
      opacity: 0.2;
    }
    .react-pdf__Page__textContent span {
      color: transparent;
    }
    .react-pdf__Page__annotations .annotationLayer {
      opacity: 0.2;
    }
    .react-pdf__Page__annotations .annotationLayer section {
      pointer-events: auto;
    }
  `;

  // Styles
  const toolbarStyle = {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: currentTheme.toolbar,
    borderBottom: `1px solid ${currentTheme.border}`,
    gap: "8px",
    flexWrap: "wrap",
    minHeight: "48px",
  };
  const buttonStyle = {
    padding: "6px 12px",
    backgroundColor: currentTheme.button,
    border: `1px solid ${currentTheme.border}`,
    borderRadius: "4px",
    color: currentTheme.text,
    fontSize: "14px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };
  const viewerStyle = {
    flex: 1,
    backgroundColor: currentTheme.background,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "auto",
    position: "relative",
  };
  const pageInputStyle = {
    width: "60px",
    padding: "4px 8px",
    border: `1px solid ${currentTheme.border}`,
    borderRadius: "4px",
    backgroundColor: currentTheme.button,
    color: currentTheme.text,
    textAlign: "center",
    fontSize: "14px",
  };
  const searchBarStyle = {
    position: "absolute",
    top: "60px",
    right: "20px",
    backgroundColor: currentTheme.toolbar,
    border: `1px solid ${currentTheme.border}`,
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: isSearchOpen ? "flex" : "none",
    alignItems: "center",
    gap: "8px",
    zIndex: 10,
  };
  const searchInputStyle = {
    padding: "6px 12px",
    border: `1px solid ${currentTheme.border}`,
    borderRadius: "4px",
    backgroundColor: currentTheme.button,
    color: currentTheme.text,
    fontSize: "14px",
    width: "200px",
  };

  useEffect(() => {
    if (!viewerRef.current) return;
    const handleResize = () => {
      setViewerSize({
        width: viewerRef.current.offsetWidth,
        height: viewerRef.current.offsetHeight,
      });
    };
    handleResize();
    const resizeObserver = new window.ResizeObserver(handleResize);
    resizeObserver.observe(viewerRef.current);
    return () => resizeObserver.disconnect();
  }, [viewerRef]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: currentTheme.background,
        color: currentTheme.text,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Inject custom CSS for PDF layers */}
      <style dangerouslySetInnerHTML={{ __html: pdfStyles }} />

      {/* Toolbar */}
      {enableToolbar && (
        <div style={toolbarStyle}>
          {/* Navigation Controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <button
              style={{ ...buttonStyle, opacity: currentPage <= 1 ? 0.5 : 1 }}
              onClick={firstPage}
              disabled={currentPage <= 1}
              title="First Page"
            >
              <FontAwesomeIcon icon={faBackwardFast} />
            </button>
            <button
              style={{ ...buttonStyle, opacity: currentPage <= 1 ? 0.5 : 1 }}
              onClick={prevPage}
              disabled={currentPage <= 1}
              title="Previous Page"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <input
              type="number"
              value={currentPage}
              onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
              style={pageInputStyle}
              min="1"
              max={totalPages}
            />
            <span
              style={{ fontSize: "14px", color: currentTheme.textSecondary }}
            >
              of {totalPages}
            </span>
            <button
              style={{
                ...buttonStyle,
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
              onClick={nextPage}
              disabled={currentPage >= totalPages}
              title="Next Page"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            <button
              style={{
                ...buttonStyle,
                opacity: currentPage >= totalPages ? 0.5 : 1,
              }}
              onClick={lastPage}
              disabled={currentPage >= totalPages}
              title="Last Page"
            >
              <FontAwesomeIcon icon={faForwardFast} />
            </button>
          </div>
          <div
            style={{
              width: "1px",
              height: "24px",
              backgroundColor: currentTheme.border,
            }}
          />
          {/* Zoom Controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <button style={buttonStyle} onClick={zoomOut} title="Zoom Out">
              <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
            </button>
            <span
              style={{
                fontSize: "14px",
                minWidth: "60px",
                textAlign: "center",
              }}
            >
              {zoomLevel}%
            </span>
            <button style={buttonStyle} onClick={zoomIn} title="Zoom In">
              <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
            </button>
          </div>
          <div
            style={{
              width: "1px",
              height: "24px",
              backgroundColor: currentTheme.border,
            }}
          />
          {/* Fit Controls */}
          <select
            value={fitMode}
            onChange={(e) => handleFitMode(e.target.value)}
            style={{
              ...buttonStyle,
              paddingRight: "24px",
            }}
          >
            <option value="fit-width">Fit Width</option>
            <option value="fit-height">Fit Height</option>
            {/* <option value="fit-page">Fit Page</option>
            <option value="actual-size">Actual Size</option> */}
          </select>
          <div style={{ flex: 1 }} />
          {/* Action Buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {/* {enableSearch && (
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: isSearchOpen
                    ? currentTheme.accent
                    : currentTheme.button,
                  color: isSearchOpen ? "white" : currentTheme.text,
                }}
                onClick={toggleSearch}
                title="Search (Ctrl+F)"
              >
                🔍
              </button>
            )}
            {customToolbarButtons.map((button, index) => (
              <button
                key={index}
                style={buttonStyle}
                onClick={button.onClick}
                title={button.title}
              >
                {button.icon} {button.label}
              </button>
            ))} */}
            {enableDownload && (
              <button
                style={buttonStyle}
                onClick={handleDownload}
                title="Download"
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            )}
            {enablePrint && (
              <button style={buttonStyle} onClick={handlePrint} title="Print">
                <FontAwesomeIcon icon={faPrint} />
              </button>
            )}
            <button
              style={buttonStyle}
              onClick={handleThemeToggle}
              title="Theme"
            >
              {themeState === "light" ? (
                <FontAwesomeIcon icon={faMoon} />
              ) : (
                <FontAwesomeIcon icon={faSun} />
              )}
            </button>
          </div>
        </div>
      )}
      {/* Search Bar */}
      {/* <div style={searchBarStyle}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search in document..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          style={searchInputStyle}
        />
        <button style={buttonStyle} onClick={toggleSearch} title="Close Search">
          ✕
        </button>
      </div> */}
      {/* PDF Viewer Area */}
      <div ref={viewerRef} style={viewerStyle}>
        {error ? (
          <div
            style={{
              textAlign: "center",
              color: "#dc3545",
              padding: "40px",
            }}
          >
            <h3>Error Loading PDF</h3>
            <p>{error}</p>
          </div>
        ) : pdfUrl ? (
          // <div
          //   style={{
          //     border: `1px solid ${currentTheme.border}`,
          //     backgroundColor: "white",
          //     boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          //     margin: "20px",
          //     minHeight: "600px",
          //     width: "90%",
          //     maxWidth: "800px",
          //     display: "flex",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     position: "relative",
          //     overflow: "auto",
          //   }}
          // >
          <Document
            file={pdfUrl}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            options={{
              cMapUrl: "//unpkg.com/pdfjs-dist@2.16.105/cmaps/",
              cMapPacked: true,
              standardFontDataUrl:
                "//unpkg.com/pdfjs-dist@2.16.105/standard_fonts/",
            }}
            loading={
              <div
                style={{ color: currentTheme.textSecondary, padding: "40px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: `4px solid ${currentTheme.border}`,
                    borderTop: `4px solid ${currentTheme.accent}`,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "16px",
                  }}
                />
                <p>Loading PDF...</p>
                <style>
                  {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                </style>
              </div>
            }
            error={<div>Error loading PDF.</div>}
          >
            <Page
              pageNumber={currentPage}
              scale={zoomLevel / 100}
              width={fitMode === "fit-width" ? viewerSize.width : undefined}
              // height={fitMode === "fit-height" ? viewerSize.height : undefined}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={
                <div
                  style={{
                    color: currentTheme.textSecondary,
                    padding: "40px",
                  }}
                >
                  <p>Loading Page...</p>
                </div>
              }
            />
          </Document>
        ) : (
          // </div>
          <div
            style={{
              textAlign: "center",
              color: currentTheme.textSecondary,
              padding: "40px",
            }}
          >
            <h3>No PDF Loaded</h3>
            <p>Provide a PDF URL to display</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
